import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showToast } from "#root/components/GlobalToaster";
import { useUpdateTenant, useTenantById, Base64UploadFileDto } from "#root/queries/tenants";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import OrganizationForm, { OrganizationFormData } from "../OrganizationForm";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { DialogBox } from "#root/components/DialogBox";
import { FILE_KINDS, FILE_REFERENCE_TYPES } from "#root/utils/media";
import { fileToBase64, urlToFile } from "#root/services/utils";
import { OrganizationRegular } from "@fluentui/react-icons";
import { ROUTES } from "#root/routes/constants";
import { AxiosError } from "axios";

const EditOrganization: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tenantId = searchParams.get('organizationId');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [formData, setFormData] = useState<OrganizationFormData | null>(null);

    const { data: tenantResponse, isLoading: isTenantLoading } = useTenantById({ id: tenantId! });
    const updateTenantMutation = useUpdateTenant();

    const tenant = tenantResponse?.item;

    useEffect(() => {
        const processLogo = async () => {
            setIsLoading(true);
            try {
                const file = tenant?.logo?.url
                    ? await urlToFile(tenant.logo.url, tenant.logo.id).catch(() => undefined)
                    : undefined;

                setFormData({
                    organizationName: tenant!.organizationName,
                    organizationId: tenant!.organizationId,
                    organizationLogo: file,
                    organizationType: tenant!.type!
                });
            } catch (error) {
                console.error('Error processing logo:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (tenant) {
            processLogo();
        }
    }, [tenant]);

    const handleSubmit = async (data: OrganizationFormData) => {
        setFormData(data);
        setShowConfirmDialog(true);
    };

    const handleConfirm = async () => {
        if (!tenantId || !formData) return;
        setIsSubmitting(true);

        try {
            let logoData: Base64UploadFileDto | undefined = undefined;
            if (formData.organizationLogo && !formData.organizationLogo.id) {
                const base64Content = await fileToBase64(formData.organizationLogo);
                logoData = {
                    name: formData.organizationLogo.name,
                    type: formData.organizationLogo.type,
                    content: base64Content,
                    referenceId: tenantId,
                    referenceType: FILE_REFERENCE_TYPES.TENANT,
                    kind: FILE_KINDS.LOGO
                };
            }

            // First update the tenant details
            await updateTenantMutation.mutateAsync({
                tenant: {
                    id: tenantId,
                    organizationName: formData.organizationName,
                    organizationId: formData.organizationId
                },
                logo: logoData,
                deleteLogo: !formData.organizationLogo
            });

            showToast('Organization updated successfully', 'success');
            navigate(ROUTES.ROOT);
        } catch (error) {
            if (error instanceof AxiosError) {
                const errorMessage = error.response?.data?.Message || "Failed to update organization";
                const cleanedMessage = errorMessage.replace(/\s*\([^)]*\)/g, '');
                showToast(cleanedMessage, "error");
            }
            else {
                showToast("Failed to update organization", "error");
            }
        } finally {
            setIsSubmitting(false);
            setShowConfirmDialog(false);
        }
    };

    return (
        <div className="edit-organization">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>Edit Organization <div className="divider"></div></h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={isTenantLoading || isLoading}
                isEmpty={!tenant}
                emptyMessage="Organization not found"
                emptyIcon={<OrganizationRegular />}
            >
                {tenant && formData && (
                    <OrganizationForm
                        initialData={formData}
                        onSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        submitButtonText="Edit Organization"
                        editMode={true}
                    />
                )}
            </LoadingWrapper>

            <DialogBox
                open={showConfirmDialog}
                onOpenChange={setShowConfirmDialog}
                title="Edit Organization"
                primaryButtonText="Confirm"
                onPrimaryButtonClick={handleConfirm}
                isPrimaryButtonLoading={isSubmitting}
                isPrimaryButtonDisabled={isSubmitting}
            >
                Are you sure you want to edit the Organization details?
            </DialogBox>
        </div>
    );
};

export default EditOrganization;